import { gql } from "@apollo/client";
import { SearchSelect } from "./SearchSelect";
import client from "../../../graphqlClient";
import { RestockVariationSearchSelect_SearchRestockVariationsQuery, RestockVariationSearchSelect_SearchRestockVariationsQueryVariables } from "../../../../codegen/graphql";
import { useCallback } from "react";
import { formatCurrency } from "../../../utils/currency";
import cx from "classnames";

const SEARCH_RESTOCK_VARIATIONS = gql`
  query RestockVariationSearchSelect_SearchRestockVariations(
    $supplierProductId: ID!
    $supplierVariationSku: String!
  ) {
    searchRestockVariations(supplierProductId: $supplierProductId, supplierVariationSku: $supplierVariationSku) {
      id
      quantity
      availableStock
      cost {
        unitCost
        totalCost
      }
      restockShipmentProduct {
        id
        externalOrderId
        sellerCompany
        shippingTrackingNumber
      }
    }
  }
`;

export type RestockVariationSearchSelect_RestockVariationOption = RestockVariationSearchSelect_SearchRestockVariationsQuery['searchRestockVariations'][number];

export default function RestockVariation_SearchSelect({
  selectedRestockVariation,
  onRestockVariationChange,
  supplierProductId,
  supplierVariationSku,
}: {
  selectedRestockVariation: RestockVariationSearchSelect_RestockVariationOption | null;
  onRestockVariationChange: (restockVariation: RestockVariationSearchSelect_RestockVariationOption | null) => void;
  supplierProductId: string;
  supplierVariationSku: string;
}) {

  const loadRestockVariations = useCallback(async (searchText: string) => {
    const result = await client.query<
      RestockVariationSearchSelect_SearchRestockVariationsQuery,
      RestockVariationSearchSelect_SearchRestockVariationsQueryVariables
    >({
      query: SEARCH_RESTOCK_VARIATIONS,
      variables: { supplierProductId, supplierVariationSku },
      fetchPolicy: 'network-only'
    });

    if (!result.data) {
      throw result.errors;
    }

    return result.data.searchRestockVariations;
  }, [supplierProductId, supplierVariationSku]);

  return (
    <SearchSelect
      withSearch={false}
      loadOptions={loadRestockVariations}
      renderOption={(restockVariation, selectCallback) => (
        <RestockVariationOption
          restockVariation={restockVariation}
          isRenderSelected={false}
          selectCallback={selectCallback}
        />
      )}
      renderSelected={(restockVariation) => (
        <RestockVariationOption
          restockVariation={restockVariation}
          isRenderSelected={true}
        />
      )}
      value={selectedRestockVariation || null}
      onSelect={(selectedRestockVariation) => {
        onRestockVariationChange(selectedRestockVariation);
      }}
    />
  )
}


function RestockVariationOption({
  restockVariation,
  isRenderSelected = false,
  selectCallback,
}: {
  restockVariation: RestockVariationSearchSelect_RestockVariationOption,
  isRenderSelected: boolean,
  selectCallback?: () => void
}) {
  const cost = restockVariation.cost;
  const availableStock = restockVariation.availableStock;
  const restockProduct = restockVariation.restockShipmentProduct;
  
  return (
    <div
      className={cx(
        'flex-1',
        !isRenderSelected && 'px-4 py-2'
      )}
    >
      <div
        className={cx(
          'flex-1 border-1 border-gray-300 rounded-md p-2 flex justify-between',
          isRenderSelected && 'border-none p-0',
          !isRenderSelected && 'hover:bg-blue-50 hover:border-blue-500',
          !isRenderSelected && 'cursor-pointer'
        )}
        onClick={selectCallback || undefined}
      >
        <div className="flex-1">
          <p className="text-lg font-medium text-gray-900 mb-1">
            Restock Shipment Variation
          </p>
          {restockProduct && (
            <>
              <p className="text-sm text-gray-600">
                Order ID: {restockProduct.externalOrderId || 'N/A'}
              </p>
              {restockProduct.sellerCompany && (
                <p className="text-sm text-gray-600">
                  Seller: {restockProduct.sellerCompany}
                </p>
              )}
              {restockProduct.shippingTrackingNumber && (
                <p className="text-sm text-gray-600">
                  Tracking: {restockProduct.shippingTrackingNumber}
                </p>
              )}
            </>
          )}
          <p className="text-sm text-gray-700 mt-2">
            Quantity: {restockVariation.quantity} | 
            Available Stock: {availableStock}
          </p>
        </div>
        <div className="flex-none ml-4">
          {cost && (
            <>
              <p className="text-sm text-right text-gray-600">
                Unit: {formatCurrency(cost.unitCost)}
              </p>
              <p className="text-lg font-medium text-right text-gray-900">
                Total: {formatCurrency(cost.totalCost)}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}